import { apiClient } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiResponse } from "@/api/types";

/** The kinds a label exists for. Anything else shows its raw kind rather than nothing. */
export type NotificationKind = string;

export interface NotificationDto {
  notification_id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  entity: string | null;
  entity_id: string | null;
  link: string | null;
  actor_user_id: string | null;
  read_on: string | null;
  created_on: string;
}

const E = endpoints.notifications;

/**
 * `portal` reads the same inbox through the client-only route
 * (/portal/notifications) — the staff route refuses a client session.
 */
const urls = (portal: boolean) =>
  portal
    ? { root: E.portalRoot, unread: E.portalUnreadCount, read: E.portalRead }
    : { root: E.root, unread: E.unreadCount, read: E.read };

export const notificationsService = {
  async inbox(unreadOnly = false, limit = 30, portal = false): Promise<{ items: NotificationDto[]; unread: number }> {
    const { data } = await apiClient.get<ApiResponse<{ items: NotificationDto[]; unread: number }>>(urls(portal).root, { params: { unreadOnly, limit } });
    return data.data;
  },
  async unreadCount(portal = false): Promise<{ unread: number }> {
    const { data } = await apiClient.get<ApiResponse<{ unread: number }>>(urls(portal).unread);
    return data.data;
  },
  /** Absent ids = mark everything read. */
  async markRead(ids?: string[], portal = false): Promise<{ marked: number }> {
    const { data } = await apiClient.post<ApiResponse<{ marked: number }>>(urls(portal).read, ids ? { ids } : {});
    return data.data;
  },
};

export const KIND_LABEL: Record<string, string> = {
  TASK_ASSIGNED: "Assigned",
  TASK_VERSION_SUBMITTED: "For review",
  TASK_REVIEWED: "Reviewed",
  TASK_REMINDER: "Reminder",
  TASK_ESCALATED: "Escalated",
  TASK_DEADLINE_MISSED: "Missed",
  FOLLOWUP_ESCALATION: "Escalation",
  FOLLOWUP_REPORT: "Report",
  INSTALMENT_DUE: "Payment",
  SERVICE_EXPIRY: "Renewal",
  COMMUNITY_DECISION: "Community",
  COMMUNITY_FEED: "Community",
  COMMUNITY_CHAPTER: "Chapter",
  COMMUNITY_WORKSHOP: "Workshop",
  COMMUNITY_MODERATION: "Moderation",
  CALENDAR_REMINDER: "Reminder",
};

export const KIND_TONE: Record<string, string> = {
  TASK_ASSIGNED: "bg-blue-100 text-blue-800",
  TASK_VERSION_SUBMITTED: "bg-purple-100 text-purple-800",
  TASK_REVIEWED: "bg-green-100 text-green-800",
  TASK_REMINDER: "bg-amber-100 text-amber-800",
  TASK_ESCALATED: "bg-red-100 text-red-800",
  TASK_DEADLINE_MISSED: "bg-red-100 text-red-800",
  FOLLOWUP_ESCALATION: "bg-red-100 text-red-800",
  FOLLOWUP_REPORT: "bg-slate-100 text-slate-700",
  INSTALMENT_DUE: "bg-amber-100 text-amber-800",
  SERVICE_EXPIRY: "bg-amber-100 text-amber-800",
};
